/**
 * API 调用流程动画 - API Flow Animation
 * 
 * 展示客户端-服务器-数据库的请求响应流程
 * 可视化 GET/POST/PUT/DELETE 请求
 * 
 * 适合：编程教学、后端开发入门
 * 
 * @date 2026-03-19
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// API 请求类型
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// API 调用步骤
interface APIStep {
  id: string;
  label: string;
  labelEn: string;
  icon: string;
  x: number;
  y: number;
  color: string;
}

// 请求配置
interface RequestConfig {
  method: HttpMethod;
  endpoint: string;
  description: string;
  statusCode: number;
  statusText: string;
}

// 组件属性
interface APIFlowAnimationProps {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  requestConfig?: RequestConfig;
}

// 默认请求配置
const DEFAULT_REQUEST: RequestConfig = {
  method: 'GET',
  endpoint: '/api/users/123',
  description: '获取用户信息',
  statusCode: 200,
  statusText: 'OK',
};

// API 流程步骤
const FLOW_STEPS: APIStep[] = [
  {
    id: 'client',
    label: '客户端',
    labelEn: 'Client',
    icon: '📱',
    x: 200,
    y: 540,
    color: '#4A90E2',
  },
  {
    id: 'server',
    label: '服务器',
    labelEn: 'Server',
    icon: '🖥️',
    x: 960,
    y: 540,
    color: '#10B981',
  },
  {
    id: 'database',
    label: '数据库',
    labelEn: 'Database',
    icon: '🗄️',
    x: 1720,
    y: 540,
    color: '#F59E0B',
  },
];

// HTTP 方法配置
const METHOD_CONFIGS: Record<HttpMethod, { color: string; label: string }> = {
  GET: { color: '#10B981', label: '查询' },
  POST: { color: '#4A90E2', label: '创建' },
  PUT: { color: '#F59E0B', label: '更新' },
  DELETE: { color: '#EF4444', label: '删除' },
};

// 绘制步骤节点
const StepNode: React.FC<{
  step: APIStep;
  opacity: number;
  scale: number;
}> = ({ step, opacity, scale }) => {
  return (
    <div
      style={{
        position: 'absolute',
        left: step.x - 100,
        top: step.y - 80,
        width: 200,
        height: 160,
        backgroundColor: `${step.color}20`,
        border: `3px solid ${step.color}`,
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity,
        transform: `scale(${scale})`,
        boxShadow: `0 0 30px ${step.color}40`,
        overflow: 'hidden',
      }}
    >
      <div style={{ fontSize: 44, marginBottom: 6, lineHeight: 1 }}>{step.icon}</div>
      <div style={{ fontSize: 26, fontWeight: 'bold', color: step.color }}>
        {step.label}
      </div>
      <div style={{ fontSize: 22, color: '#9CA3AF', marginTop: 4 }}>
        {step.labelEn}
      </div>
    </div>
  );
};

// 绘制连接箭头
const FlowArrow: React.FC<{
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  progress: number;
  color: string;
  label: string;
  labelPosition: 'top' | 'bottom';
}> = ({ startX, startY, endX, endY, progress, color, label, labelPosition }) => {
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <svg
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <defs>
        <marker
          id={`arrow-${color.replace('#', '')}`}
          markerWidth="12"
          markerHeight="12"
          refX="10"
          refY="6"
          orient="auto"
        >
          <path
            d="M 0 0 L 12 6 L 0 12 L 3 6 Z"
            fill={color}
            opacity={progress > 0.9 ? 1 : 0}
          />
        </marker>
        <linearGradient
          id={`gradient-${color.replace('#', '')}`}
          gradientUnits="userSpaceOnUse"
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
        >
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset={`${progress * 100}%`} stopColor={color} stopOpacity={1} />
          <stop offset={`${progress * 100}%`} stopColor={color} stopOpacity={0} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      
      {/* 箭头线 */}
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke={`url(#gradient-${color.replace('#', '')})`}
        strokeWidth="4"
        markerEnd={`url(#arrow-${color.replace('#', '')})`}
      />
      
      {/* 标签 */}
      {progress > 0.3 && (
        <text
          x={midX}
          y={labelPosition === 'top' ? midY - 25 : midY + 40}
          textAnchor="middle"
          fill={color}
          fontSize="22"
          fontWeight="bold"
          opacity={Math.min(1, (progress - 0.3) * 2)}
        >
          {label}
        </text>
      )}
    </svg>
  );
};

// 绘制请求/响应数据包
const DataPacket: React.FC<{
  x: number;
  y: number;
  opacity: number;
  method: HttpMethod;
  endpoint: string;
  isResponse?: boolean;
}> = ({ x, y, opacity, method, endpoint, isResponse = false }) => {
  const methodConfig = METHOD_CONFIGS[method];
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x - 150,
        top: y - 40,
        width: 300,
        backgroundColor: '#1F2937',
        border: `2px solid ${methodConfig.color}`,
        borderRadius: 12,
        padding: '12px 16px',
        opacity,
        transform: `translateY(${(1 - opacity) * 20}px)`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <span
          style={{
            backgroundColor: methodConfig.color,
            color: '#FFFFFF',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 20,
            fontWeight: 'bold',
            marginRight: 8,
          }}
        >
          {method}
        </span>
        <span style={{ color: '#FFFFFF', fontSize: 24, marginBottom: 5 }}>{endpoint}</span>
      </div>
      <div style={{ color: '#9CA3AF', fontSize: 22 }}>
        {isResponse ? '响应数据' : methodConfig.label}
      </div>
    </div>
  );
};

// 绘制状态码指示器
const StatusCodeIndicator: React.FC<{
  statusCode: number;
  statusText: string;
  opacity: number;
  y: number;
}> = ({ statusCode, statusText, opacity, y }) => {
  const isSuccess = statusCode >= 200 && statusCode < 300;
  const color = isSuccess ? '#10B981' : '#EF4444';
  
  return (
    <div
      style={{
        position: 'absolute',
        left: 960 - 100,
        top: y,
        width: 200,
        backgroundColor: `${color}20`,
        border: `2px solid ${color}`,
        borderRadius: 12,
        padding: '16px',
        opacity,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 32, fontWeight: 'bold', color }}>
        {statusCode}
      </div>
      <div style={{ fontSize: 22, color: '#FFFFFF', marginTop: 4 }}>
        {statusText}
      </div>
    </div>
  );
};

// 主组件
export const APIFlowAnimation: React.FC<APIFlowAnimationProps> = ({
  primaryColor = '#4A90E2',
  secondaryColor = '#10B981',
  backgroundColor = '#0F1419',
  requestConfig = DEFAULT_REQUEST,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 标题动画
  const titleOpacity = spring({
    frame,
    fps,
    config: { damping: 20 },
  });

  const titleY = interpolate(
    spring({ frame, fps, config: { damping: 20 } }),
    [0, 1],
    [-50, 0]
  );

  // 步骤出现动画
  const stepAnimations = FLOW_STEPS.map((_, index) => ({
    opacity: spring({
      frame: frame - index * 15,
      fps,
      config: { damping: 20 },
    }),
    scale: spring({
      frame: frame - index * 15,
      fps,
      config: { damping: 15, stiffness: 100 },
    }),
  }));

  // 箭头进度动画
  const arrow1Progress = Math.min(
    1,
    spring({
      frame: frame - 60,
      fps,
      config: { damping: 20 },
    })
  );

  const arrow2Progress = Math.min(
    1,
    spring({
      frame: frame - 90,
      fps,
      config: { damping: 20 },
    })
  );

  const arrow3Progress = Math.min(
    1,
    spring({
      frame: frame - 150,
      fps,
      config: { damping: 20 },
    })
  );

  const arrow4Progress = Math.min(
    1,
    spring({
      frame: frame - 180,
      fps,
      config: { damping: 20 },
    })
  );

  // 数据包动画
  const requestPacket = {
    x: interpolate(
      spring({ frame: frame - 75, fps, config: { damping: 20 } }),
      [0, 1],
      [300, 860]
    ),
    opacity: spring({
      frame: frame - 70,
      fps,
      config: { damping: 20 },
    }),
  };

  const dbPacket = {
    x: interpolate(
      spring({ frame: frame - 105, fps, config: { damping: 20 } }),
      [0, 1],
      [1060, 1620]
    ),
    opacity: spring({
      frame: frame - 100,
      fps,
      config: { damping: 20 },
    }),
  };

  const dbResponsePacket = {
    x: interpolate(
      spring({ frame: frame - 165, fps, config: { damping: 20 } }),
      [0, 1],
      [1620, 1060]
    ),
    opacity: spring({
      frame: frame - 160,
      fps,
      config: { damping: 20 },
    }),
  };

  const responsePacket = {
    x: interpolate(
      spring({ frame: frame - 195, fps, config: { damping: 20 } }),
      [0, 1],
      [860, 300]
    ),
    opacity: spring({
      frame: frame - 190,
      fps,
      config: { damping: 20 },
    }),
  };

  // 状态码显示
  const statusCodeOpacity = spring({
    frame: frame - 140,
    fps,
    config: { damping: 20 },
  });

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          width: '100%',
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 'bold',
            color: primaryColor,
            margin: 0,
            marginBottom: 12,
          }}
        >
          API 调用流程
        </h1>
        <h2 style={{ fontSize: 32, color: '#9CA3AF', margin: 0 }}>
          API Request-Response Flow
        </h2>
      </div>

      {/* 请求信息 */}
      <div
        style={{
          position: 'absolute',
          top: 230,
          width: '100%',
          textAlign: 'center',
          opacity: spring({ frame: frame - 30, fps, config: { damping: 20 } }),
        }}
      >
        <div
          style={{
            display: 'inline-block',
            backgroundColor: `${METHOD_CONFIGS[requestConfig.method].color}20`,
            border: `2px solid ${METHOD_CONFIGS[requestConfig.method].color}`,
            borderRadius: 12,
            padding: '10px 24px',
          }}
        >
          <span
            style={{
              fontSize: 24,
              fontWeight: 'bold',
              color: METHOD_CONFIGS[requestConfig.method].color,
              marginRight: 12,
            }}
          >
            {requestConfig.method}
          </span>
          <span style={{ fontSize: 24, color: '#FFFFFF' }}>
            {requestConfig.endpoint}
          </span>
          <div style={{ fontSize: 22, color: '#9CA3AF', marginTop: 4 }}>
            {requestConfig.description}
          </div>
        </div>
      </div>

      {/* 步骤节点 */}
      {FLOW_STEPS.map((step, index) => (
        <StepNode
          key={step.id}
          step={step}
          opacity={stepAnimations[index].opacity}
          scale={stepAnimations[index].scale}
        />
      ))}

      {/* 连接箭头 - 请求 */}
      <FlowArrow
        startX={300}
        startY={540}
        endX={860}
        endY={540}
        progress={arrow1Progress}
        color={primaryColor}
        label="HTTP 请求"
        labelPosition="top"
      />

      <FlowArrow
        startX={1060}
        startY={540}
        endX={1620}
        endY={540}
        progress={arrow2Progress}
        color={secondaryColor}
        label="查询数据"
        labelPosition="top"
      />

      {/* 连接箭头 - 响应 */}
      <FlowArrow
        startX={1620}
        startY={540}
        endX={1060}
        endY={540}
        progress={arrow3Progress}
        color={secondaryColor}
        label="返回结果"
        labelPosition="bottom"
      />

      <FlowArrow
        startX={860}
        startY={540}
        endX={300}
        endY={540}
        progress={arrow4Progress}
        color={primaryColor}
        label="HTTP 响应"
        labelPosition="bottom"
      />

      {/* 数据包动画 */}
      {requestPacket.opacity > 0 && (
        <DataPacket
          x={requestPacket.x}
          y={380}
          opacity={requestPacket.opacity}
          method={requestConfig.method}
          endpoint={requestConfig.endpoint}
        />
      )}

      {dbPacket.opacity > 0 && (
        <DataPacket
          x={dbPacket.x}
          y={380}
          opacity={dbPacket.opacity}
          method={requestConfig.method}
          endpoint={requestConfig.endpoint}
        />
      )}

      {dbResponsePacket.opacity > 0 && (
        <DataPacket
          x={dbResponsePacket.x}
          y={700}
          opacity={dbResponsePacket.opacity}
          method={requestConfig.method}
          endpoint={requestConfig.endpoint}
          isResponse
        />
      )}

      {responsePacket.opacity > 0 && (
        <DataPacket
          x={responsePacket.x}
          y={700}
          opacity={responsePacket.opacity}
          method={requestConfig.method}
          endpoint={requestConfig.endpoint}
          isResponse
        />
      )}

      {/* 状态码指示器 */}
      {statusCodeOpacity > 0 && (
        <StatusCodeIndicator
          statusCode={requestConfig.statusCode}
          statusText={requestConfig.statusText}
          opacity={statusCodeOpacity}
          y={780}
        />
      )}

      {/* 底部说明 */}
      <div
        style={{
          position: 'absolute',
          bottom: 30,
          width: '100%',
          textAlign: 'center',
          opacity: spring({
            frame: frame - 200,
            fps,
            config: { damping: 20 },
          }),
        }}
      >
        <div style={{ fontSize: 24, color: '#9CA3AF' }}>
          客户端 → 服务器 → 数据库 → 服务器 → 客户端
        </div>
        <div style={{ fontSize: 24, color: '#6B7280', marginTop: 6 }}>
          完整的 API 请求响应周期
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 导出示例配置
export const exampleAPIRequests: Record<string, RequestConfig> = {
  getUser: {
    method: 'GET',
    endpoint: '/api/users/123',
    description: '获取用户信息',
    statusCode: 200,
    statusText: 'OK',
  },
  createUser: {
    method: 'POST',
    endpoint: '/api/users',
    description: '创建新用户',
    statusCode: 201,
    statusText: 'Created',
  },
  updateUser: {
    method: 'PUT',
    endpoint: '/api/users/123',
    description: '更新用户信息',
    statusCode: 200,
    statusText: 'OK',
  },
  deleteUser: {
    method: 'DELETE',
    endpoint: '/api/users/123',
    description: '删除用户',
    statusCode: 204,
    statusText: 'No Content',
  },
};
